import { DateTime } from "luxon";
import { addMethod, object, string } from "yup";
import { listOfUSAStates } from "./listOfUSAStates";
import { EDUCATION_LEVEL, DOB_FORMAT } from "@portal/app/_common/types/customerDetail";
import isEmpty from "lodash/isEmpty";
import { Address } from "@portal/app/_common/types/address";

declare module "yup" {
  interface StringSchema {
    /**
     * When a field is not required, undefined is allowed. This method
     * transforms empty strings to undefined so that empty strings are
     * treated as if the field was not filled out. This is useful for
     * forms that default a value to "" so that the form is "controlled".
     */
    allowEmptyString(): this;
  }
}

addMethod(string, "allowEmptyString", function () {
  return this.transform((value) => (value === "" ? undefined : value));
});

export const startingOrTrailingCommaRegex = /^[^,].*[^,]$/;

const streetOptional = string()
  .trim()
  .allowEmptyString()
  .matches(startingOrTrailingCommaRegex, "Address can't start/end with a comma");
const cityOptional = string()
  .trim()
  .allowEmptyString()
  .matches(startingOrTrailingCommaRegex, "City can't start/end with a comma");
const stateOptional = string()
  .trim()
  .allowEmptyString()
  .length(2, 'Enter the state with 2 letters, like "TX"')
  .oneOf(Object.keys(listOfUSAStates), "Enter a valid US state");
const zipOptional = string()
  .matches(/^\d+$/, {
    excludeEmptyString: true,
    message: "A valid ZIP code is required",
  })
  .matches(/^\d{5}$/g, {
    excludeEmptyString: true,
    message: "Enter a 5-digit ZIP code",
  })
  .trim();
const street2Optional = string().trim();
const optionalRules = {
  nameFirst: string().trim(),

  nameLast: string().trim(),

  email: string().email("Email address is invalid").trim(),

  phone: string()
    .matches(/^[2-9]/g, {
      excludeEmptyString: true,
      message: "Start with your area code, not 0 or 1.",
    })
    .matches(/^\d{10}$/g, {
      excludeEmptyString: true,
      message: "Your phone number needs to be 10 digits",
    })
    .trim(),

  dob: string()
    .transform((value) => value ?? "")
    .allowEmptyString()
    .test("is-valid-dob", "Date of birth is invalid", (value) => {
      if (!value) {
        return true;
      }
      const dob = DateTime.fromFormat(value, DOB_FORMAT);
      return dob.isValid && dob.year < DateTime.local().year;
    })
    .test("is-too-recent", `Date of birth is too recent`, (value) => {
      if (!value) {
        return true;
      }
      const dob = DateTime.fromFormat(value, DOB_FORMAT);
      return dob.isValid && DateTime.local().diff(dob, "years").years >= 10;
    }),

  street: streetOptional,

  street2: street2Optional,

  city: cityOptional,

  state: stateOptional,

  zip: zipOptional,

  address: object()
    .transform((value) => (isEmpty(value) ? undefined : value))
    .test(
      "is-valid-address",
      "Address must have street, city, state, and zip",
      (address: Address | undefined) =>
        !isEmpty(address)
          ? object({
              street: streetOptional.required("Street address is required"),
              street2: street2Optional,
              city: cityOptional.required("City is required"),
              state: stateOptional.required("State is required"),
              zip: zipOptional.required("ZIP code is required"),
            })
              .required()
              .isValidSync(address)
          : true
    ),

  educationLevel: string().trim().allowEmptyString().oneOf(Object.values(EDUCATION_LEVEL)),
};

export const yupRules = {
  optional: optionalRules,
  required: {
    nameFirst: optionalRules.nameFirst.required("First name is required"),
    nameLast: optionalRules.nameLast.required("Last name is required"),
    email: optionalRules.email.required("Email address is required"),
    phone: optionalRules.phone.required("Phone is required"),
    dob: optionalRules.dob.required("Your date of birth is required"),
    street: optionalRules.street.required("Street address is required"),
    city: optionalRules.city.required("City is required"),
    state: optionalRules.state.required("State is required"),
    zip: optionalRules.zip.required("ZIP code is required"),
    address: optionalRules.address.required("Address is required"),
    educationLevel: optionalRules.educationLevel.required(
      "Your highest education level attained is required"
    ),
  },
};
